import * as THREE from "three";
import { LayerItem } from "../data-structures/layer-item";
import { LayerItemType } from "../data-structures/layer-item-type";
import { LayerStatus } from "../data-structures/layer-status";
import { MeasurementType } from "../data-structures/measure-type";
import { ViewType } from "../data-structures/view-type";
import { Tools } from "./tools";
import { Annotation } from "./annotation";
import { Measure } from "./measure";
import { UnifiedLineMeasure } from "./unified-line-measure";
import DxfParser from "../third-party/dxf-parser/dxf-parser";

declare const proj4: any;

/**
 * Manager responsible for managing CAD-related functionality including
 * measurements, annotations, and CAD layers.
 */
export class CadManager {
	public measurements: any[] = [];
	public unifiedLineMeasurements: any[] = [];
	public annotations: Annotation[] = [];
	public cadLayers: LayerItem[] = [];
	public activeCadLayer: LayerItem | null = null;

	public constructor(private readonly viewer: any) {}

	/**
	 * Adds a new CAD layer with the specified name.
	 * @param layerName - Name of the layer to create
	 */
	public addLayer(layerName: string): void {
		if (this.cadLayers.find((x: LayerItem) => x.name == layerName) != null) {
			return;
		}
		const layer: LayerItem = {
			id: layerName,
			name: layerName,
			layerType: LayerItemType.Cad,
			visible: true,
			layerStatus: LayerStatus.ProcessedAndLoaded
		};
		this.cadLayers.push(layer);
		this.viewer.updateLayers();
	}

	/**
	 * Deletes a CAD layer and all its associated measurements.
	 * @param layerName - Name of the layer to delete
	 */
	public deleteLayer(layerName: string): void {
		const layer = this.cadLayers.find((x: LayerItem) => x.name == layerName);
		if (!layer) {
			return;
		}

		// Remove regular measurements
		let i = 0;
		while (i < this.measurements.length) {
			const measurement = this.measurements[i];

			if (measurement.layerName == layer.name) {
				this.removeMeasurement(measurement);
			} else {
				i++;
			}
		}

		// Remove unified line measurements
		let j = 0;
		while (j < this.unifiedLineMeasurements.length) {
			const unifiedLineMeasurement = this.unifiedLineMeasurements[j];
			if (unifiedLineMeasurement.layerName == layer.name) {
				this.removeUnifiedLineMeasurement(unifiedLineMeasurement);
			} else {
				j++;
			}
		}

		this.cadLayers.splice(this.cadLayers.indexOf(layer), 1);

		if (this.activeCadLayer?.id == layer.id) {
			this.activeCadLayer = null;
		}

		this.viewer.updateLayers();
	}

	/**
	 * Initiates line drawing mode for the specified tool type.
	 * @param lineType - The type of line/measurement to draw (default: Line)
	 */
	public startLineDrawing(lineType: Tools = Tools.Line): void {
		if (lineType == Tools.Selection || lineType == Tools.Annotation) {
			return;
		}
		if (this.viewer.createdMeasurement) {
			return;
		}
		if (this.viewer.selectedElement) {
			this.viewer.selectedElement.hover = false;
			this.viewer.selectedElement.selected = false;
		}

		// Create default layer if there is no active layer
		if (!this.activeCadLayer) {
			const defaultLayer: LayerItem = {
				id: "Default",
				name: "Default",
				visible: true,
				layerType: LayerItemType.Cad,
				layerStatus: LayerStatus.ProcessedAndLoaded
			};
			this.cadLayers.push(defaultLayer);
			this.activeCadLayer = defaultLayer;
			this.viewer.updateLayers();
		} else if (!this.activeCadLayer.visible) {
			return;
		}

		let maxMarkers: number | undefined = undefined;
		if (lineType == Tools.Point) {
			maxMarkers = 1;
		} else if (lineType == Tools.Height) {
			maxMarkers = 2;
		} else if (lineType == Tools.Circle) {
			maxMarkers = 3;
		}

		const measurement = new Measure();
		measurement.projectCoordinateSystem = this.viewer.projectCoordinateSystem;
		measurement.unit = this.viewer.unit;
		measurement.hasMapCoordinates = this.viewer.viewType == ViewType.View2D;
		measurement.showDistances = lineType === Tools.Line;
		measurement.showHeight = lineType === Tools.Height;
		measurement.showCircle = lineType === Tools.Circle;
		measurement.showEdges = lineType !== Tools.Circle;
		measurement.showArea = lineType === Tools.Polygon;
		measurement.showCoordinates = lineType === Tools.Point;
		measurement.closed = lineType === Tools.Point || lineType === Tools.Polygon;
		measurement.maxMarkers = maxMarkers;
		measurement.name = "Measurement";
		measurement.color = new THREE.Color(255, 255, 255);
		measurement.layerName = this.activeCadLayer?.name;

		if (lineType == Tools.Height) {
			measurement.displayedMeasurements = [MeasurementType.Height];
		} else if (lineType == Tools.Circle) {
			measurement.displayedMeasurements = [];
		} else if (lineType == Tools.Polygon) {
			measurement.displayedMeasurements = [MeasurementType.Area3D];
		} else {
			measurement.displayedMeasurements = [MeasurementType.Distance3D];
		}

		measurement.addMarker(new THREE.Vector3(0, 0, 0));

		this.addMeasurement(measurement);
		this.viewer.createdMeasurement = measurement;
	}

	public stopLineDrawing(removeIfIncomplete = true): void {
		if (!this.viewer.createdMeasurement) {
			return;
		}

		if (this.viewer.createdMeasurement) {
			// Remove measurement if only 1 point was added or if maxMarkers was not reached
			const measurement = this.viewer.createdMeasurement as any;
			if (removeIfIncomplete && (measurement.showArea || measurement.showDistances) && measurement.maxMarkers !== 1) {
				measurement.removeMarker(measurement.points.length - 1);
			}
			if (removeIfIncomplete && (this.viewer.createdMeasurement.elementType == "point" || (measurement.maxMarkers && measurement.points.length !== measurement.maxMarkers) || (measurement.points.length == 1 && measurement.showDistances))) {
				this.removeMeasurement(measurement);
			}

			this.viewer.createdMeasurement.selected = false;
		}
		this.viewer.createdMeasurement = null;
	}

	public addMeasurement(measure: any): void {
		this.viewer.measurementsGroup.add(measure);
		this.measurements.push(measure);
		this.viewer.externalImageManager.updateOverlayRenderStateFor(measure);
	}

	public addUnifiedLineMeasurement(measure: any): void {
		this.viewer.unifiedLineMeasurementsGroup.add(measure);
		this.unifiedLineMeasurements.push(measure);
		this.viewer.externalImageManager.updateOverlayRenderStateFor(measure);
	}

	public removeMeasurement(measure: any): void {
		this.viewer.measurementsGroup.remove(measure);
		const index = this.measurements.indexOf(measure);
		if (index > -1) {
			this.measurements.splice(index, 1);
		}
	}

	public removeUnifiedLineMeasurement(measure: any): void {
		this.viewer.unifiedLineMeasurementsGroup.remove(measure);
		const index = this.unifiedLineMeasurements.indexOf(measure);
		if (index > -1) {
			this.unifiedLineMeasurements.splice(index, 1);
		}
	}

	public convertMeasurements(toMapCoordinates: boolean): void {
		if (!this.viewer.projectCoordinateSystem) {
			// Do not convert if input coordinate system is not known
			return;
		}

		for (const measurement of this.measurements) {
			const isInMapCoordinates = measurement.hasMapCoordinates;

			// Measurement is already in correct coordinate space
			if (isInMapCoordinates == toMapCoordinates) {
				continue;
			}
			measurement.hasMapCoordinates = toMapCoordinates;

			for (const point of measurement.points) {
				const pos = point.position;
				if (toMapCoordinates) {
					const newPoint = proj4(this.viewer.projectCoordinateSystem, this.viewer.mapCoordinateSystem).forward([pos.x, pos.y, pos.z]);
					pos.set(newPoint[0], newPoint[1], newPoint[2]);
				} else {
					const newPoint = proj4(this.viewer.projectCoordinateSystem, this.viewer.mapCoordinateSystem).inverse([pos.x, pos.y, pos.z]);
					pos.set(newPoint[0], newPoint[1], newPoint[2]);
				}
			}
		}

		for (const unifiedLineMeasurement of this.unifiedLineMeasurements) {
			const isInMapCoordinates = unifiedLineMeasurement.hasMapCoordinates;

			// Measurement is already in correct coordinate space
			if (isInMapCoordinates == toMapCoordinates) {
				continue;
			}
			unifiedLineMeasurement.hasMapCoordinates = toMapCoordinates;

			for (const point of unifiedLineMeasurement.points) {
				const pos = point.position;
				if (toMapCoordinates) {
					const newPoint = proj4(this.viewer.projectCoordinateSystem, this.viewer.mapCoordinateSystem).forward([pos.x, pos.y, pos.z]);
					pos.set(newPoint[0], newPoint[1], newPoint[2]);
				} else {
					const newPoint = proj4(this.viewer.projectCoordinateSystem, this.viewer.mapCoordinateSystem).inverse([pos.x, pos.y, pos.z]);
					pos.set(newPoint[0], newPoint[1], newPoint[2]);
				}
			}
			unifiedLineMeasurement.updateVisuals();
		}
	}

	public updateMeasurementsCoordinateSystems(): void {
		for (const measurement of this.measurements) {
			measurement.projectCoordinateSystem = this.viewer.projectCoordinateSystem;
			measurement.unit = this.viewer.unit;
		}
	}

	public cleanup(): void {
		// Remove measurements
		while (this.measurements.length > 0) {
			this.removeMeasurement(this.measurements[0]);
		}

		while (this.unifiedLineMeasurements.length > 0) {
			this.removeUnifiedLineMeasurement(this.unifiedLineMeasurements[0]);
		}

		// Remove annotations
		while (this.annotations.length > 0) {
			this.removeAnnotation(this.annotations[0]);
		}

		this.cadLayers = [];
		this.activeCadLayer = null;
	}

	public createAnnotation(position: THREE.Vector3): Annotation {
		const annotation = new Annotation();
		annotation.position.set(position.x, position.y, position.z);
		this.viewer.annotationsGroup.add(annotation);
		this.annotations.push(annotation);
		return annotation;
	}

	public removeAnnotation(annotation: Annotation): void {
		const index = this.annotations.indexOf(annotation);
		if (index > -1) {
			this.annotations.splice(index, 1);
		}
		if (annotation.parent) {
			annotation.parent.remove(annotation);
		}
	}

	public convertAnnotations(toMapCoordinates: boolean): void {
		if (!this.viewer.projectCoordinateSystem) {
			return;
		}

		for (const annotation of this.annotations) {
			const isInMapCoordinates = (annotation as any).hasMapCoordinates;

			// Annotation is already in correct coordinate space
			if (isInMapCoordinates == toMapCoordinates) {
				continue;
			}
			(annotation as any).hasMapCoordinates = toMapCoordinates;

			const pos = annotation.position;
			if (toMapCoordinates) {
				const newPoint = proj4(this.viewer.projectCoordinateSystem, this.viewer.mapCoordinateSystem).forward([pos.x, pos.y, pos.z]);
				pos.set(newPoint[0], newPoint[1], newPoint[2]);
			} else {
				const newPoint = proj4(this.viewer.projectCoordinateSystem, this.viewer.mapCoordinateSystem).inverse([pos.x, pos.y, pos.z]);
				pos.set(newPoint[0], newPoint[1], newPoint[2]);
			}
		}
	}

	public updateMeasurements(): void {
		const camera = this.viewer.scene.getActiveCamera();
		const measurements = this.measurements;

		const renderAreaSize = this.viewer.renderer.getSize(new THREE.Vector2());
		const clientWidth = renderAreaSize.width;
		const clientHeight = renderAreaSize.height;

		// make size independant of distance
		for (const measure of measurements) {
			measure.lengthUnit = this.viewer.lengthUnit;
			measure.lengthUnitDisplay = this.viewer.lengthUnitDisplay;
			measure.update();

			this.updateAzimuth(measure);

			// spheres
			for(const sphere of measure.spheres){
				const distance = camera.position.distanceTo(sphere.getWorldPosition(new THREE.Vector3()));
				const pr = (window as any).Potree.Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
				const scale = (8 / pr);
				sphere.scale.set(scale, scale, scale);
			}

			// labels
			const labels = measure.edgeLabels;
			for(const label of labels){
				const distance = camera.position.distanceTo(label.getWorldPosition(new THREE.Vector3()));
				const pr = (window as any).Potree.Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
				let scale = (70 / pr);

				if((window as any).Potree.debug.scale){
					scale = ((window as any).Potree.debug.scale / pr);
				}

				label.scale.set(scale, scale, scale);
			}

			// coordinate labels
			for (let j = 0; j < measure.coordinateLabels.length; j++) {
				const label = measure.coordinateLabels[j];
				const sphere = measure.spheres[j];

				const distance = camera.position.distanceTo(sphere.getWorldPosition(new THREE.Vector3()));

				const screenPos = sphere.getWorldPosition(new THREE.Vector3()).clone().project(camera);
				screenPos.x = Math.round((screenPos.x + 1) * clientWidth / 2);
				screenPos.y = Math.round((-screenPos.y + 1) * clientHeight / 2);
				screenPos.z = 0;
				screenPos.y -= 30;

				let labelPos = new THREE.Vector3(
					(screenPos.x / clientWidth) * 2 - 1,
					-(screenPos.y / clientHeight) * 2 + 1,
					0.5 );
				labelPos.unproject(camera);
				if(this.viewer.scene.cameraMode == (window as any).Potree.CameraMode.PERSPECTIVE) {
					const direction = labelPos.sub(camera.position).normalize();
					labelPos = new THREE.Vector3().addVectors(
						camera.position, direction.multiplyScalar(distance));

				}
				label.position.copy(labelPos);
				const pr = (window as any).Potree.Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
				const scale = (70 / pr);
				label.scale.set(scale, scale, scale);
			}

			{ // area label
				const label = measure.areaLabel;
				const distance = label.position.distanceTo(camera.position);
				const pr = (window as any).Potree.Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);

				const scale = (70 / pr);
				label.scale.set(scale, scale, scale);
			}

			{ // radius label
				const label = measure.circleRadiusLabel;
				const distance = label.position.distanceTo(camera.position);
				const pr = (window as any).Potree.Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);

				const scale = (70 / pr);
				label.scale.set(scale, scale, scale);
			}

			{ // edges
				const materials = [
					measure.circleRadiusLine.material,
					...measure.edges.map( (e: any) => e.material),
					measure.circleLine.material,
				];

				for(const material of materials){
					material.resolution.set(clientWidth, clientHeight);
				}
			}
		}

		for (const unifiedLineMeasure of this.unifiedLineMeasurements) {
			unifiedLineMeasure.updateSphereScales(camera, clientWidth, clientHeight);
			unifiedLineMeasure.updateLabelScales(camera, clientWidth, clientHeight);
		}

		//Independent scale for annotations
		for (const annotation of this.annotations) {
			if(camera.isOrthographicCamera){
				const s = 0.02;
				annotation.circleCenter.scale.set(s,s,s);
			}else{
				const distance = camera.position.distanceTo(annotation.getWorldPosition(new THREE.Vector3()));
				const pr = (window as any).Potree.Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
				const scale = (15 / pr);
				annotation.circleCenter.scale.set(scale, scale, scale);
			}
		}

		this.viewer.profileManager.updateHoverMarkerScales(camera, clientWidth, clientHeight);
		this.viewer.externalImageManager.updateMarkerScales(camera, clientWidth, clientHeight);
		this.viewer.volumeManager.updateLabelScales(camera, clientWidth, clientHeight);
	}

	private updateAzimuth(measure: any): void {
		const azimuth = measure.azimuth;

		const isOkay = measure.points.length === 2;

		azimuth.node.visible = isOkay && measure.showAzimuth;

		if(!azimuth.node.visible){
			return;
		}

		const camera = this.viewer.scene.getActiveCamera();
		const renderAreaSize = this.viewer.renderer.getSize(new THREE.Vector2());
		const width = renderAreaSize.width;
		const height = renderAreaSize.height;

		const [p0, p1] = measure.points;
		const r = p0.position.distanceTo(p1.position);
		const northVec = (window as any).Potree.Utils.getNorthVec(p0.position, r, this.viewer.getProjection());
		const northPos = p0.position.clone().add(northVec);

		azimuth.center.position.copy(p0.position);
		azimuth.center.scale.set(2, 2, 2);

		azimuth.center.visible = false;

		{ // north
			azimuth.north.position.copy(northPos);
			azimuth.north.scale.set(2, 2, 2);

			const distance = azimuth.north.position.distanceTo(camera.position);
			const pr = (window as any).Potree.Utils.projectedRadius(1, camera, distance, width, height);

			const scale = (5 / pr);
			azimuth.north.scale.set(scale, scale, scale);
		}

		{ // target
			azimuth.target.position.copy(p1.position);
			azimuth.target.position.z = azimuth.north.position.z;

			const distance = azimuth.target.position.distanceTo(camera.position);
			const pr = (window as any).Potree.Utils.projectedRadius(1, camera, distance, width, height);

			const scale = (5 / pr);
			azimuth.target.scale.set(scale, scale, scale);
		}

		azimuth.circle.position.copy(p0.position);
		azimuth.circle.scale.set(r, r, r);
		azimuth.circle.material.resolution.set(width, height);

		// to target
		azimuth.centerToTarget.geometry.setPositions([
			0, 0, 0,
			...p1.position.clone().sub(p0.position).toArray(),
		]);
		azimuth.centerToTarget.position.copy(p0.position);
		azimuth.centerToTarget.geometry.verticesNeedUpdate = true;
		azimuth.centerToTarget.geometry.computeBoundingSphere();
		azimuth.centerToTarget.computeLineDistances();
		azimuth.centerToTarget.material.resolution.set(width, height);

		// to target ground
		azimuth.centerToTargetground.geometry.setPositions([
			0, 0, 0,
			p1.position.x - p0.position.x,
			p1.position.y - p0.position.y,
			0,
		]);
		azimuth.centerToTargetground.position.copy(p0.position);
		azimuth.centerToTargetground.geometry.verticesNeedUpdate = true;
		azimuth.centerToTargetground.geometry.computeBoundingSphere();
		azimuth.centerToTargetground.computeLineDistances();
		azimuth.centerToTargetground.material.resolution.set(width, height);

		// to north
		azimuth.centerToNorth.geometry.setPositions([
			0, 0, 0,
			northPos.x - p0.position.x,
			northPos.y - p0.position.y,
			0,
		]);
		azimuth.centerToNorth.position.copy(p0.position);
		azimuth.centerToNorth.geometry.verticesNeedUpdate = true;
		azimuth.centerToNorth.geometry.computeBoundingSphere();
		azimuth.centerToNorth.computeLineDistances();
		azimuth.centerToNorth.material.resolution.set(width, height);

		// label
		const radians = (window as any).Potree.Utils.computeAzimuth(p0.position, p1.position, this.viewer.getProjection());
		let degrees = THREE.MathUtils.radToDeg(radians);
		if(degrees < 0){
			degrees = 360 + degrees;
		}
		const txtDegrees = `${degrees.toFixed(2)}°`;
		const labelDir = northPos.clone().add(p1.position).multiplyScalar(0.5).sub(p0.position);
		if(labelDir.length() > 0){
			labelDir.z = 0;
			labelDir.normalize();
			const labelVec = labelDir.clone().multiplyScalar(r);
			const labelPos = p0.position.clone().add(labelVec);
			azimuth.label.position.copy(labelPos);
		}
		azimuth.label.setText(txtDegrees);
		const distance = azimuth.label.position.distanceTo(camera.position);
		const pr = (window as any).Potree.Utils.projectedRadius(1, camera, distance, width, height);
		const scale = (70 / pr);
		azimuth.label.scale.set(scale, scale, scale);
	}

	public updateAnnotationTool(): void {
		// calculate objects intersecting the picking ray
		const intersects = this.viewer.raycaster.intersectObjects(this.viewer.annotations);

		if (intersects.length > 0) {
			this.viewer.intersectionPoint.copy(intersects[0].point);
		}

		if (!this.viewer.wasPointerDown && this.viewer.isPointerDown) {
			this.viewer.movingAnnotation = (intersects.find((x: any) => x.object.parent instanceof Annotation)?.object.parent as any) ?? null;
			if (this.viewer.movingAnnotation) {
				//Disable camera dragging
				this.viewer.inputHandler.drag = null;
			}
		}

		if (this.viewer.movingAnnotation && this.viewer.selectedElement === this.viewer.movingAnnotation && this.viewer.lastValidIntersectionPoint) {
			const pos = this.viewer.lastValidIntersectionPoint;
			if (pos) {
				this.viewer.setHelperPosition(pos);
				if(this.viewer.viewType == ViewType.View2D){ // Do not move points by Z axis
					pos.z = this.viewer.movingAnnotation.position.z;
				}
				this.viewer.movingAnnotation.position.set(pos.x, pos.y, pos.z);
			}
		}else{
			this.viewer.hideHelperPosition();
		}

		if (this.viewer.wasPointerDown && !this.viewer.isPointerDown) {
			this.viewer.movingAnnotation = null;
			this.viewer.onToolCancel.emit();
		}
	}

	/**
	 * Loads and parses a DXF file, creating measurements and layers from the data.
	 * @param url - URL of the DXF file to load
	 * @returns Promise resolving to true if successful, false otherwise
	 */
	public async loadDXF(url: string): Promise<boolean> {
		const loader = new THREE.FileLoader(THREE.DefaultLoadingManager);
		try {
			const result = await loader.loadAsync(url);
			const parser = new DxfParser();
			const data = parser.parse(result);

			if (data && data.entities) {
				for (const entry of data.entities) {
					if (!this.cadLayers.find((l) => l.name == entry.layer)) {
						this.cadLayers.push({
							id: entry.layer,
							name: entry.layer,
							visible: true,
							layerType: LayerItemType.Cad,
							layerStatus: LayerStatus.ProcessedAndLoaded
						});
					}

					const layerInfo = data.tables.layer.layers[entry.layer];
					const color = layerInfo && layerInfo.color ? layerInfo.color : 0xFFFFFF;

					if (entry.type === "POLYLINE") {
						const points = entry.vertices.map(e => new THREE.Vector3(e.x, e.y, entry.elevation === 0 ? e.z : (entry.elevation ?? e.z)));
						const measure = new UnifiedLineMeasure();
						measure.setPoints(points);
						measure.color = new THREE.Color(color);
						measure.layerName = entry.layer;
						measure.closed = entry.shape;
						measure.createVisuals();
						this.addUnifiedLineMeasurement(measure);
					} else if (entry.type === "POINT") {
						const measure = new Measure();
						measure.color.setHex(color);
						measure.layerName = entry.layer;
						measure.showDistances = false;
						measure.showAngles = false;
						measure.closed = true;
						measure.maxMarkers = 1;
						measure.addMarker(new THREE.Vector3(entry.position.x, entry.position.y, entry.position.z));
						this.addMeasurement(measure);
					} else if (entry.type === "CIRCLE") {
						const measure = new Measure();
						measure.layerName = entry.layer;
						measure.showEdges = false;
						measure.showCircle = true;
						measure.maxMarkers = 3;
						measure.color.setHex(color);
						const center = new THREE.Vector3(entry.center.x, entry.center.y, entry.center.z);
						measure.addMarker(new THREE.Vector3(-entry.radius, 0, 0).add(center));
						measure.addMarker(new THREE.Vector3(+entry.radius, 0, 0).add(center));
						measure.addMarker(new THREE.Vector3(0, entry.radius, 0).add(center));
						this.addMeasurement(measure);
					}
				}

				//Cleanup empty cad layers
				let c = 0;
				while (c < this.cadLayers.length) {
					const cadLayer = this.cadLayers[c];
					const numberOfCadLayers = this.measurements.filter((m) => m.layerName == cadLayer.name).length + this.unifiedLineMeasurements.filter((m) => m.layerName == cadLayer.name).length;
					if (numberOfCadLayers == 0) {
						this.cadLayers.splice(c, 1);
					} else {
						c++;
					}
				}

				//Set active cad layer
				if (this.cadLayers.length > 0) {
					this.activeCadLayer = this.cadLayers[0];
				}

				//Add information for coordinate system to measurements and convert them to map coordinates if necessary
				this.updateMeasurementsCoordinateSystems();

				if (this.viewer.viewType == ViewType.View2D) {
					this.convertMeasurements(true);
				}

				this.viewer.updateLayers();
				return true;
			} else {
				return false;
			}
		} catch (e) {
			console.error(`Error parsing dxf: ${e}`);
			return false;
		}
	}
}
